import { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import { microsoftConsumerSecurityClient } from 'client-factories/MicrosoftConsumerSecurityClientFactoryV1';
import { pageDataService } from 'common/utilities/PageDataService';
import { localeIgnoreCaseEquals } from 'common/utilities/String';
import { isNil, orderBy } from 'lodash';
import { CreditAlert, CreditRecommendationType, UserFeedback } from 'mcsclient/models';
import { resetAlerts } from 'store/alerts/alertsSlice';
import { ApiCallResultStatus } from 'store/apiCallResultStatus';
import { createAsyncApiThunk, createOneOffAsyncApiThunk } from 'store/reduxHelpers';

import { getCreditRecommendationByType } from './creditMonitoringSelectors';
import { ensureId } from './identityMonitoringHelpers';
import { getITPFamilyMemberFromCsid } from './identityMonitoringSelectors';
import { ensureITPFamilyMemberWithCsid } from './identityMonitoringSlice';
import { IIdentityMonitoringState } from './identityMonitoringState';

interface IUpdateCreditAlertAsyncParams {
  csid: string;
  id: string;
  options: {
    userFeedback?: UserFeedback;
    isArchived?: boolean;
  };
}

export const registerCreditMonitoringSubscriberAsync = createAsyncApiThunk(
  'creditMonitoring/registerCreditMonitoringSubscriberAsync',
  (csid: string | undefined) =>
    ensureId(csid, async (ensuredCsid) =>
      pageDataService.isCurrentUser(ensuredCsid)
        ? microsoftConsumerSecurityClient.identityTheftMonitoringSubscription.registerCreditMonitoringSubscriber()
        : microsoftConsumerSecurityClient.familyIdentityTheftMonitoringSubscription.registerCreditMonitoringSubscriber(ensuredCsid)
    )
);

function orderCreditAlerts(alerts: CreditAlert[] | undefined) {
  return orderBy(
    // sort alerts by archivedDateTime and alertDateTime before storing in state
    alerts,
    [(alert) => alert.archivedDateTime, (alert) => alert.alertDateTime],
    ['desc', 'desc']
  );
}

export const getCreditSummaryAsync = createOneOffAsyncApiThunk(
  'creditMonitoring/getCreditSummaryAsync',
  async (csid: string) => {
    const { alerts, report, score } = pageDataService.isCurrentUser(csid)
      ? await microsoftConsumerSecurityClient.credit.getCreditSummary()
      : await microsoftConsumerSecurityClient.familyCredit.getCreditSummary(csid);

    return { alerts, report, score };
  },
  ({ identityMonitoring }, csid) =>
    getITPFamilyMemberFromCsid(identityMonitoring, csid)?.getCreditSummaryApiStatus ?? ApiCallResultStatus.DEFAULT
);

export const handleGetCreditSummaryAsync = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder
    .addCase(getCreditSummaryAsync.pending, (state, { meta }) => {
      const csid = meta.arg;
      const member = ensureITPFamilyMemberWithCsid(state, csid);
      member.getCreditSummaryApiStatus = ApiCallResultStatus.LOADING;
    })
    .addCase(getCreditSummaryAsync.fulfilled, (state, { payload, meta }) => {
      const csid = meta.arg;
      const member = ensureITPFamilyMemberWithCsid(state, csid);

      member.creditSummary = payload;
      member.creditSummary.alerts = orderCreditAlerts(payload.alerts);

      member.getCreditSummaryApiStatus = ApiCallResultStatus.SUCCESS;
    })
    .addCase(getCreditSummaryAsync.rejected, (state, { meta }) => {
      const csid = meta.arg;
      const member = ensureITPFamilyMemberWithCsid(state, csid);
      member.getCreditSummaryApiStatus = ApiCallResultStatus.FAILED;
    });
};

export const getCreditAlertByIdAsync = createAsyncApiThunk(
  'creditMonitoring/getCreditAlertByIdAsync',
  (args: { csid: string; alertId: string }) => {
    const { csid, alertId } = args;
    return pageDataService.isCurrentUser(csid)
      ? microsoftConsumerSecurityClient.credit.getCreditAlertById(alertId)
      : microsoftConsumerSecurityClient.familyCredit.getCreditAlertById(csid, alertId);
  }
);

export const handleGetCreditAlertByIdAsync = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder.addCase(getCreditAlertByIdAsync.fulfilled, (state, { payload, meta }) => {
    const { csid, alertId: argAletId } = meta.arg;
    const member = ensureITPFamilyMemberWithCsid(state, csid);

    const alert = payload;

    if (!localeIgnoreCaseEquals(alert?.id, argAletId)) {
      // if API returns 404
      return; // TODO: investigate why it 404 is considered fulfilled
    }

    //
    // Upsert alert in creditSummary
    //

    member.creditSummary ??= {};
    const alertIndex = member.creditSummary.alerts?.findIndex((a) => localeIgnoreCaseEquals(a.id, alert.id)) ?? -1;

    if (alertIndex > -1 && member.creditSummary.alerts) {
      // update alert in summary
      member.creditSummary.alerts[alertIndex] = alert;
    } else {
      // add alert to summary
      member.creditSummary.alerts ??= [];
      member.creditSummary.alerts.push(alert);
    }
    member.creditSummary.alerts = orderCreditAlerts(member.creditSummary.alerts);
  });
};

export const updateCreditAlertAsync = createAsyncApiThunk(
  'creditmonitoring/updateCreditAlertAsync',
  ({ csid, id, options }: IUpdateCreditAlertAsyncParams, thunkApi) => {
    const { userFeedback, isArchived } = options;

    return ensureId(csid, async (ensuredCsid) => {
      const alert = pageDataService.isCurrentUser(ensuredCsid)
        ? // Current user
          await microsoftConsumerSecurityClient.credit.updateCreditMonitoringAlert(id, {
            body: { userFeedback, isArchived },
          })
        : // Family member
          await microsoftConsumerSecurityClient.familyCredit.updateCreditMonitoringAlert(csid, id, {
            body: { userFeedback, isArchived },
          });

      // Only reset alerts if the alert was archived.
      if (!isNil(options?.isArchived)) {
        await thunkApi.dispatch(resetAlerts());
      }

      return alert;
    });
  }
);

export const handleUpdateCreditAlertAsync = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder.addCase(updateCreditAlertAsync.fulfilled, (state, { payload, meta }) => {
    const { csid, id } = meta.arg;
    const member = ensureITPFamilyMemberWithCsid(state, csid);

    // The action still took place, but we don't yet have credit summary in the store.
    // That's okay, because the next time the summary is retrieved, the updated alert will be included.
    if (!member?.creditSummary?.alerts) {
      return;
    }

    const alertIndex = member.creditSummary.alerts.findIndex((a) => a.id === id);

    const { userFeedback, isArchived, archivedDateTime } = payload;

    if (alertIndex >= 0) {
      const alertDetails = member.creditSummary.alerts[alertIndex];
      alertDetails.userFeedback = userFeedback;
      alertDetails.isArchived = isArchived;

      // The backend will always have the correct date, regardless.
      // If isArchived didn't actually change, this is a throwaway value.
      alertDetails.archivedDateTime = archivedDateTime;

      member.creditSummary.alerts = orderCreditAlerts(member.creditSummary.alerts);
    }
  });
};

/**
 * Updates the state of a credit alert recommendation
 */
export const completeCreditRecommendationAsync = createAsyncApiThunk(
  'creditmonitoring/completeCreditRecommendationAsync',
  ({
    alertId,
    recommendationType,
    isCompleted,
    csid,
  }: {
    alertId: string;
    recommendationType: CreditRecommendationType;
    isCompleted: boolean;
    csid: string | undefined;
  }) =>
    ensureId(csid, (ensuredCsid) =>
      pageDataService.isCurrentUser(ensuredCsid)
        ? microsoftConsumerSecurityClient.credit.completeRecommendation(alertId, recommendationType, {
            body: { isCompleted },
          })
        : microsoftConsumerSecurityClient.familyCredit.completeRecommendation(ensuredCsid, alertId, recommendationType, {
            body: { isCompleted },
          })
    )
);

export const handleCompleteCreditRecommendationAsync = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  const getRecommendation = (
    state: IIdentityMonitoringState,
    {
      alertId,
      recommendationType,
      csid,
    }: {
      alertId: string;
      recommendationType: CreditRecommendationType;
      csid: string | undefined;
    }
  ) => getCreditRecommendationByType(state, csid, alertId, recommendationType);

  builder
    .addCase(completeCreditRecommendationAsync.pending, (state, { meta }) => {
      const { isCompleted } = meta.arg;
      const recommendation = getRecommendation(state, meta.arg);
      if (recommendation) {
        // Optimistically set it first
        recommendation.isCompleted = isCompleted;
        recommendation.updateRecommendationStateApiStatus = {
          requestId: meta.requestId,
          status: ApiCallResultStatus.LOADING,
        };
      }
    })
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    .addCase(completeCreditRecommendationAsync.fulfilled, (state, { meta, payload }) => {
      const { isCompleted } = meta.arg;
      const recommendation = getRecommendation(state, meta.arg);
      if (recommendation) {
        const { updateRecommendationStateApiStatus } = recommendation;
        if (updateRecommendationStateApiStatus?.requestId === meta.requestId) {
          updateRecommendationStateApiStatus.status = ApiCallResultStatus.SUCCESS;
          recommendation.isCompleted = Boolean(isCompleted);
        }
      }
    })
    .addCase(completeCreditRecommendationAsync.rejected, (state, { meta }) => {
      const { isCompleted } = meta.arg;
      const recommendation = getRecommendation(state, meta.arg);
      if (recommendation) {
        const { updateRecommendationStateApiStatus } = recommendation;
        if (updateRecommendationStateApiStatus?.requestId === meta.requestId) {
          updateRecommendationStateApiStatus.status = ApiCallResultStatus.FAILED;
          recommendation.isCompleted = !isCompleted;
        }
      }
    });
};
